import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { DriverProfile } from 'src/app/demo/models/common-data-types';
import { DriversService } from 'src/app/demo/service/drivers.service';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { ShowOutstandingDocumentsComponent } from '../../common/show-outstanding-documents/show-outstanding-documents.component';
import { Message, MessageService } from 'primeng/api';
import { ShowDocumentsComponent } from '../../common/show-documents/show-documents.component';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DocumentsService } from 'src/app/demo/service/documents.service';
import { DocumentEntities } from 'src/app/demo/constants/document-entities';
import { VerifyCustomerKycDocumentComponent } from '../../common/verify-customer-kyc-document/verify-customer-kyc-document.component';
import { ProgressBarModule } from 'primeng/progressbar';
import { NgxTranslateModule } from 'src/app/translate.module';
import { languages } from 'src/app/demo/constants/languages';
import { LanguageService } from 'src/app/demo/service/language.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomerProfileDocumentViewComponent } from '../../common/customer-profile-document-view/customer-profile-document-view.component';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';

@Component({
    selector: 'app-driver-detail',
    standalone: true,
    imports: [
        CommonModule,
        CardModule,
        ButtonModule,
        MessagesModule,
        NgxTranslateModule,
        RouterModule,
        FileUploadModule,
        TooltipModule,
        ProgressBarModule,
        CalendarModule,
        InputTextModule,
        TableModule,
    ],
    templateUrl: './driver-detail.component.html',
    styleUrl: './driver-detail.component.scss',
    providers: [MessagesModule],
})
export class DriverDetailComponent implements OnInit {
    driver!: DriverProfile;
    driverId!: string;
    messages: Message[] = [];

    languages: any[] = languages;
    selectedLanguage!: string;

    blobUrl: SafeResourceUrl | null = null;
    ref: DynamicDialogRef | undefined;

    loadingDocumentView: boolean = false;
    constructor(
        private progressLoader: ProgressLoaderService,
        private driverService: DriversService,
        private dialogService: DialogService,
        private route: ActivatedRoute,
        private router: Router,
        private languageService: LanguageService,
        private translateService: TranslateService,
        private documentsService: DocumentsService,
        private sanitizer: DomSanitizer,
        private messageService: MessageService
    ) {
        this.extractDriverId();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    extractDriverId() {
        this.route.paramMap.subscribe((params) => {
            this.driverId = params.get('id')!;
        });
    }

    fetchDriverProfile() {
        this.progressLoader.show();
        this.driverService.getDriverDetails(this.driverId).subscribe({
            next: (value) => {
                this.driver = value;
                this.progressLoader.hide();
            },
            error: (err) => {
                this.progressLoader.hide();
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
            },
        });
    }

    ngOnInit(): void {
        this.fetchDriverProfile();
        this.initializeLocale();
    }

    getKycDocument(id: string) {
        this.loadingDocumentView = true;
        this.documentsService.getDocumentById(id).subscribe((res) => {
            this.loadingDocumentView = false;
            const blobUrl = URL.createObjectURL(this.getBlobItem(res));
            this.blobUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
            this.viewVehicleDocument(res.body.type, id);
        });
    }

    viewVehicleDocument(mimeType: string, documentId: string) {
        const verificationData = {
            blobUrl: this.blobUrl,
            mimeType: mimeType,
            documentId: documentId,
            entityId: this.driverId,
            entity: DocumentEntities.DRIVER,
            customerId: this.driver.customer.id,
        };
        this.ref = this.dialogService.open(VerifyCustomerKycDocumentComponent, {
            width: '85%',
            showHeader: false,
            contentStyle: { overflow: 'scroll' },
            baseZIndex: 10000,
            maximizable: false,
            data: verificationData,
        });

        this.ref.onClose.subscribe((result) => {
            this.fetchDriverProfile();
        });
    }

    openVerifiedDocument(documentId: string, mimeType: string) {
        this.loadingDocumentView = true;
        this.documentsService.getDocumentById(documentId).subscribe((res) => {
            this.loadingDocumentView = false;
            const blobUrl = URL.createObjectURL(this.getBlobItem(res));
            this.blobUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);

            const viewData = {
                blobUrl: blobUrl,
                mimeType: mimeType,
                documentId: documentId,
                title: this.translateService.instant("Driver Document")
            };

            this.ref = this.dialogService.open(
                CustomerProfileDocumentViewComponent,
                {
                    width: '85%',
                    showHeader: false,
                    contentStyle: { overflow: 'scroll' },
                    baseZIndex: 10000,
                    maximizable: false,
                    data: viewData,
                }
            );
        });
    }

    getBlobItem(res: any): Blob {
        if (res.body.type === 'application/pdf') {
            return new Blob([res.body], {
                type: 'application/pdf',
            });
        } else if (res.body.type === 'image/png') {
            return new Blob([res.body], { type: 'image/png' });
        } else if (res.body.type === 'image/jpeg') {
            return new Blob([res.body], { type: 'image/jpeg' });
        } else {
            return new Blob([res.body], {
                type: 'application/octet-stream',
            });
        }
    }

    showSuccessToast(message: string) {
        this.translateService
            .get(['Success', message])
            .subscribe((translations) => {
                this.messageService.add({
                    severity: 'success',
                    summary: translations['Success'],
                    life: 2000,
                    detail: translations.get(message),
                    key: 'tc',
                });
            });
    }

    showErrorToast(error: string) {
        this.translateService
            .get(['Error', error])
            .subscribe((translations) => {
                this.messageService.add({
                    severity: 'error',
                    summary: translations['Error'],
                    life: 2000,
                    detail: translations.get(error),
                    key: 'tc',
                });
            });
    }

    goToCustomerDetails() {
        this.router.navigate(['/home/customers/' + this.driver.customer.id]);
    }
}
